import { MISSING_VALUE } from "../components/Constants";

const SLOSH_ENCODED_COLORMAP = "ping_slosh";
const WILDFIRE_ENCODED_COLORMAP = "ping_usda_wildfire_bp";
const TERRAFUSE_WILDFIRE_ENCODED_COLORMAP = "ping_terrafuse_wildfire_score";
const USDA_ENHANCED_RPS_COLORMAP = "ping_usda_enhanced_rps";

const INTERMAPS_LAYER_OPACITIES = {
  "FEMA-API": 0.1,
  "FloodScope-API": 0.1,
  "NOAA-Storm-Surge-High-Tide-API": 0.35,
  "USA-Levee-Areas-API": 0.4,
};

const KATRISK_LAYER_OPACITIES = {
  "USAfloods:500yr": 0.5,
  "USAfloodscore:flood": 0.4,
  "USAsurge:1000yr": 0.45,
  "USAsurgescore:surge": 0.3,
  "NAtcwind:1000yr": 0.4,
  "NAtcwindscore:tcwind": 0.4,
};

const KATRISK_LAYER_EXTENTS = {
  "USAfloods:500yr": [
    -125.00003279606094, 23.999933450329276, -65.99994257648854,
    50.00002401503952,
  ],
  "USAfloodscore:flood": [
    -125.00003279606094, 23.999933450329276, -65.99994257648854,
    50.00002401503952,
  ],
  "USAsurge:1000yr": [
    -98.00008895350778, 23.999933450329276, -65.99994257648854,
    45.000054080093605,
  ],
  "USAsurgescore:surge": [
    -98.00008895350778, 23.999933450329276, -65.99994257648854,
    45.000054080093605,
  ],
  "NAtcwind:1000yr": [
    -119.00746136722803, 6.99240143131025, -58.990208751447085,
    48.00219492521042,
  ],
  "NAtcwindscore:tcwind": [
    -119.00746136722803, 6.99240143131025, -58.990208751447085,
    48.00219492521042,
  ],
};

export const LAYER_OPACITIES = {
  KATRISK: KATRISK_LAYER_OPACITIES,
  INTERMAPS: INTERMAPS_LAYER_OPACITIES,
  PARCEL: 0.35,
  CRIME: 0.5,
  DEFAULT: 0.7,
  PRECISELY: 0.5,
  SWISSRE_CATNET: 0.25,
};

export const COLORMAPS = {
  SLOSH: SLOSH_ENCODED_COLORMAP,
  WILDFIRE: WILDFIRE_ENCODED_COLORMAP,
  TERRAFUSE_WILDFIRE: TERRAFUSE_WILDFIRE_ENCODED_COLORMAP,
  USDA_ENHANCED_RPS: USDA_ENHANCED_RPS_COLORMAP,
};

export const LAYER_EXTENTS = {
  KATRISK: KATRISK_LAYER_EXTENTS,
};

export const SLOSH_CATEGORIES = {
  "ping-slosh-1": "us_Category1_MOM_Inundation_HIGH",
  "ping-slosh-2": "us_Category2_MOM_Inundation_HIGH",
  "ping-slosh-3": "us_Category3_MOM_Inundation_HIGH",
  "ping-slosh-4": "us_Category4_MOM_Inundation_HIGH",
  "ping-slosh-5": "us_Category5_MOM_Inundation_HIGH",
};

export const PARCEL_URL = (env: string) => {
  if (env === "local") {
    return "http://local.pingintel.com:8010/api/v1/external_tiles/parcel";
  }

  if (env === "prd") {
    return "https://api.sovfixer.com/api/v1/external_tiles/parcel";
  } else if (env === "prdeu") {
    return "https://api.eu.sovfixer.com/api/v1/external_tiles/parcel";
  } else if (env === "stg") {
    return "https://api-staging.sovfixer.com/api/v1/external_tiles/parcel";
  } else {
    return "https://api-dev.sovfixer.com/api/v1/external_tiles/parcel";
  }
};

export const MIN_ZOOM_REQUIRED_FOR_FLOOD_ZONE = 0;
export const MIN_ZOOM_REQUIRED_FOR_EAGLEVIEW = 17;
export const FLOOD_ZONE_COLOR_MAP: Record<
  string,
  [number, number, number, number]
> = {
  A: [18, 161, 161, 180],
  AE: [18, 161, 161, 180],
  AO: [18, 161, 161, 180],
  AH: [18, 161, 161, 180],
  VE: [18, 161, 161, 180],
  X: [0, 203, 136, 180],
  D: [255, 185, 46, 40],
};

export const FIRE_RISK_COLOR_MAP: Record<
  number,
  [number, number, number, number]
> = {
  1: [255, 203, 63, 180],
  2: [255, 170, 44, 180],
  3: [255, 135, 24, 180],
  4: [255, 81, 12, 180],
  5: [255, 26, 0, 180],
};

export const CRIME_LAYER_COLOR_MAP: [number, number, number, number][] = [
  [43, 131, 186, 180], // lightest
  [157, 211, 167, 180],
  [237, 248, 185, 180],
  [249, 158, 89, 180],
  [215, 25, 28, 180], // darkest
];

// export const CRIME_THRESHOLDS = [0, 80, 150, 179, 198, 200];
export const CRIME_THRESHOLDS = [0, 11, 51, 190, 199];
export const CRIME_DESCRIPTIONS = [
  "Minimal Risk - A",
  "Low Risk - B",
  "Moderate Risk - C",
  "Elevated Risk - D",
  "High Risk - F",
];
export const LEVEE_COLOR: [number, number, number, number] = [
  255, 219, 36, 180,
];
export const FLOODWAY_COLOR: [number, number, number, number] = [
  152, 184, 241, 180,
];
export const SPECIAL_FLOODWAY_COLOR: [number, number, number, number] = [
  0, 203, 136, 180,
];
export const FUTURE_CONDITIONS_COLOR: [number, number, number, number] = [
  255, 185, 180, 180,
];
export const DEFAULT_TRANSPARENT: [number, number, number, number] = [
  0, 0, 0, 0,
];

export const GEOCODING_CONFIDENCE_MAPPING = {
  point: "Point",
  street_imputed: "Street",
  route: "Route",
  postal_code: "Postal Code",
  city: "City",
  county: "County",
  country_subdivision_name: "State",
  country: "Country",
  "—": MISSING_VALUE,
};

export const CENTER_DECIMALS_USED = 8;
